import { sendBeacon, isLocalhostHost } from '@almadar/analytics';
import { findInputByAddress } from '@almadar/core';
import { z } from 'zod';

// src/runtimes.ts
var serviceRuntimes = {
  github: ["node"],
  stripe: ["node"],
  llm: ["node", "browser"],
  ml: ["node"],
  youtube: ["node"],
  twilio: ["node"],
  email: ["node"],
  webhook: ["node"],
  push: ["node"],
  calendar: ["node"],
  drive: ["node"],
  metaAds: ["node"],
  accounting: ["node"],
  banking: ["node"],
  esign: ["node"],
  docker: ["node"],
  storage: ["node"],
  queue: ["node"],
  redis: ["node"],
  oauth: ["node"],
  credentials: ["node"],
  otel: ["node"],
  cli: ["node"],
  workspace: ["node"],
  apikeys: ["node"],
  hosting: ["node"],
  registry: ["node"],
  deepagent: ["node"],
  database: ["node"],
  wikimedia: ["node"],
  iconify: ["node"],
  arxiv: ["node"],
  rigger: ["node"],
  page: ["browser"],
  knowledge: ["node", "browser"],
  analytics: ["browser"]
};

// src/types.ts
var IntegrationError = class extends Error {
  constructor(message, code = "UNKNOWN_ERROR", details) {
    super(message);
    this.name = "IntegrationError";
    this.code = code;
    this.details = details;
  }
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      integration: this.integration,
      action: this.action,
      details: this.details
    };
  }
};

// src/core/abort.ts
function isAborted(signal) {
  return signal !== void 0 && signal.aborted;
}
function cancelled() {
  return new IntegrationError("The call was cancelled", "CANCELLED");
}
async function abortable(signal, work) {
  if (signal === void 0) return work();
  if (signal.aborted) throw cancelled();
  let onAbort;
  const stopped = new Promise((_resolve, reject) => {
    onAbort = () => reject(cancelled());
    signal.addEventListener("abort", onAbort, { once: true });
  });
  try {
    return await Promise.race([work(), stopped]);
  } finally {
    if (onAbort !== void 0) signal.removeEventListener("abort", onAbort);
  }
}

// src/integrations/page/template.ts
var REFERENCE = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
function templateReferences(template) {
  return Array.from(template.matchAll(REFERENCE), (m) => m[1]);
}
function fillTemplate(template, values) {
  if (templateReferences(template).some((name) => (values[name] ?? "") === "")) return "";
  return template.replace(REFERENCE, (_, name) => values[name]);
}

// src/core/params.ts
function invalid(name, expected) {
  throw new IntegrationError(`Param '${name}' must be ${expected}`, "VALIDATION_ERROR");
}
function isRecord(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date);
}
function str(params, name) {
  const v = params[name];
  return typeof v === "string" ? v : invalid(name, "a string");
}
function optStr(params, name) {
  return params[name] === void 0 ? void 0 : str(params, name);
}
function num(params, name) {
  const v = params[name];
  return typeof v === "number" && Number.isFinite(v) ? v : invalid(name, "a number");
}
function optNum(params, name) {
  return params[name] === void 0 ? void 0 : num(params, name);
}
function bool(params, name) {
  const v = params[name];
  return typeof v === "boolean" ? v : invalid(name, "a boolean");
}
function strList(params, name) {
  const v = params[name];
  if (!Array.isArray(v)) return invalid(name, "a list of strings");
  return v.map((item) => typeof item === "string" ? item : invalid(name, "a list of strings"));
}
function idTextItems(params, name) {
  const v = params[name];
  if (!Array.isArray(v)) return invalid(name, "a list of { id, text }");
  const items = v.map((item, i) => {
    if (!isRecord(item)) return invalid(`${name}[${i}]`, "an object { id, text }");
    return { id: str(item, "id"), text: str(item, "text") };
  });
  const seen = /* @__PURE__ */ new Set();
  for (const { id } of items) {
    if (seen.has(id)) invalid(name, `a list with distinct ids; '${id}' appears twice`);
    seen.add(id);
  }
  return items;
}
function oneOf(params, name, allowed) {
  const v = str(params, name);
  const hit = allowed.find((a) => a === v);
  return hit ?? invalid(name, `one of ${allowed.join(", ")}`);
}
function fieldSpecs(params, name) {
  const shape = "{ selector?, attr?, resolve?, template?, required? }";
  const v = params[name];
  if (!isRecord(v)) return invalid(name, `a map of field name to ${shape}`);
  const out = {};
  for (const [field, spec] of Object.entries(v)) {
    if (!isRecord(spec)) return invalid(`${name}.${field}`, `an object ${shape}`);
    out[field] = {
      selector: optStr(spec, "selector"),
      attr: optStr(spec, "attr"),
      resolve: spec.resolve === void 0 ? void 0 : bool(spec, "resolve"),
      template: optStr(spec, "template"),
      required: spec.required === void 0 ? void 0 : bool(spec, "required")
    };
  }
  for (const [field, spec] of Object.entries(out)) {
    if (spec.template === void 0) continue;
    if (spec.selector !== void 0 || spec.attr !== void 0) invalid(`${name}.${field}`, "a template or a selector/attr read, not both");
    for (const ref of templateReferences(spec.template)) {
      const target = out[ref];
      if (target === void 0) invalid(`${name}.${field}.template`, `a template over declared fields; '${ref}' is not one`);
      if (target.template !== void 0) invalid(`${name}.${field}.template`, `a template over read fields; '${ref}' is itself a template`);
    }
  }
  return out;
}
function optStrMap(params, name) {
  const v = params[name];
  if (v === void 0) return void 0;
  if (!isRecord(v)) return invalid(name, "a map of name to string");
  const out = {};
  for (const [key, value] of Object.entries(v)) out[key] = typeof value === "string" ? value : invalid(`${name}.${key}`, "a string");
  return out;
}
function optScalarMap(params, name) {
  const v = params[name];
  if (v === void 0) return void 0;
  if (!isRecord(v)) return invalid(name, "a map of name to string, number or boolean");
  const out = {};
  for (const [key, value] of Object.entries(v)) {
    out[key] = typeof value === "string" || typeof value === "boolean" || typeof value === "number" && Number.isFinite(value) ? value : invalid(`${name}.${key}`, "a string, number or boolean");
  }
  return out;
}

// src/integrations/analytics/service.ts
var browserPage = {
  hostname: () => typeof window === "undefined" ? "" : window.location.hostname,
  referrer: () => typeof document === "undefined" ? "" : document.referrer,
  screenWidth: () => typeof window === "undefined" ? void 0 : window.screen?.width
};
function domain(params, page) {
  const d = optStr(params, "domain");
  return d === void 0 || d === "" ? page.hostname() : d;
}
function path(params) {
  const p = str(params, "path");
  if (!p.startsWith("/")) throw new IntegrationError(`Param 'path' must start with "/", got '${p}'`, "VALIDATION_ERROR");
  return p;
}
function createAnalyticsService(options = {}) {
  const send = options.send ?? sendBeacon;
  const page = options.page ?? browserPage;
  async function post(endpoint, payload) {
    if (endpoint === "" || isLocalhostHost(page.hostname())) return { sent: false };
    return { sent: await send(endpoint, payload) };
  }
  async function run(action, params) {
    switch (action) {
      case "pageview": {
        const endpoint = str(params, "endpoint");
        const u = path(params);
        const referrer = page.referrer();
        const width = page.screenWidth();
        return post(endpoint, {
          n: "pageview",
          d: domain(params, page),
          u,
          ...referrer !== "" ? { r: referrer } : {},
          ...width !== void 0 ? { w: width } : {}
        });
      }
      case "track": {
        const endpoint = str(params, "endpoint");
        const name = str(params, "name");
        if (name === "pageview") throw new IntegrationError(`Event name 'pageview' is reserved; call pageview`, "VALIDATION_ERROR");
        const u = path(params);
        const props = optScalarMap(params, "props");
        return post(endpoint, { n: name, d: domain(params, page), u, ...props !== void 0 ? { p: props } : {} });
      }
      default:
        throw new IntegrationError(`analytics has no action '${action}'`, "VALIDATION_ERROR");
    }
  }
  function call(action, params, context) {
    return abortable(context?.signal, () => run(action, params));
  }
  return { call };
}

// src/integrations/page/executor.ts
var LABEL_ATTR = "data-almadar-page-label";
function observed(fields) {
  const awaits = Object.values(fields).some((f) => f.required === true);
  return { childList: true, subtree: true, characterData: awaits, attributes: awaits };
}
function windowOf(doc) {
  const view = doc.defaultView;
  if (!view) throw new IntegrationError("The page executor needs a document attached to a window", "VALIDATION_ERROR");
  return view;
}
function createPageExecutor(doc) {
  const view = windowOf(doc);
  const ids = /* @__PURE__ */ new WeakMap();
  const byId = /* @__PURE__ */ new Map();
  const applied = /* @__PURE__ */ new WeakMap();
  const watches = /* @__PURE__ */ new Map();
  const reported = /* @__PURE__ */ new Map();
  let nextId = 1;
  function idOf(el) {
    const known = ids.get(el);
    if (known !== void 0) return known;
    const id = `el-${nextId++}`;
    ids.set(el, id);
    byId.set(id, new WeakRef(el));
    return id;
  }
  function elementOf(elementId) {
    const el = byId.get(elementId)?.deref();
    if (!el || !el.isConnected) throw new IntegrationError(`No page element ${elementId}`, "ELEMENT_NOT_FOUND");
    return el;
  }
  function read(el, fields) {
    const out = {};
    const asUrl = (spec, value) => spec.resolve === true && value !== "" ? new URL(value, doc.baseURI).toString() : value;
    for (const [name, spec] of Object.entries(fields)) {
      if (spec.template !== void 0) continue;
      const target = spec.selector !== void 0 ? el.querySelector(spec.selector) : el;
      if (!target) out[name] = "";
      else if (spec.attr !== void 0) out[name] = asUrl(spec, target.getAttribute(spec.attr) ?? "");
      else out[name] = asUrl(spec, (target.textContent ?? "").trim());
    }
    const read2 = { ...out };
    for (const [name, spec] of Object.entries(fields)) {
      if (spec.template !== void 0) out[name] = asUrl(spec, fillTemplate(spec.template, read2));
    }
    return { elementId: idOf(el), fields: out };
  }
  function ready(element, fields) {
    return Object.entries(fields).every(([name, spec]) => spec.required !== true || element.fields[name] !== "");
  }
  function readReady(el, fields) {
    const element = read(el, fields);
    return ready(element, fields) ? element : void 0;
  }
  function styled(el) {
    if (!(el instanceof view.HTMLElement)) {
      throw new IntegrationError(`Page element ${idOf(el)} cannot be styled`, "VALIDATION_ERROR");
    }
    return el;
  }
  function restore(el) {
    const prev = applied.get(el);
    if (!prev) return;
    el.style.display = prev.display;
    el.style.filter = prev.filter;
    el.style.outline = prev.outline;
    prev.label?.remove();
    applied.delete(el);
  }
  return {
    query(selector, fields, limit) {
      const all = Array.from(doc.querySelectorAll(selector)).flatMap((el) => readReady(el, fields) ?? []);
      return limit !== void 0 ? all.slice(0, limit) : all;
    },
    queryWhenPresent(selector, fields, limit, waitMs) {
      const readAll = () => this.query(selector, fields, limit);
      const present = () => readAll().length > 0;
      if (present()) return Promise.resolve(readAll());
      return new Promise((resolve) => {
        const done = () => {
          observer.disconnect();
          view.clearTimeout(timer);
          resolve(readAll());
        };
        const observer = new view.MutationObserver(() => {
          if (present()) done();
        });
        observer.observe(doc.body, observed(fields));
        const timer = view.setTimeout(done, waitMs);
      });
    },
    watch(watchId, selector, fields, onElement) {
      this.unwatch(watchId);
      const seen = reported.get(watchId) ?? /* @__PURE__ */ new WeakSet();
      reported.set(watchId, seen);
      const report = () => {
        for (const el of Array.from(doc.querySelectorAll(selector))) {
          if (seen.has(el)) continue;
          const element = readReady(el, fields);
          if (element === void 0) continue;
          seen.add(el);
          onElement(element);
        }
      };
      const observer = new view.MutationObserver(report);
      observer.observe(doc.body, observed(fields));
      watches.set(watchId, { observer, seen });
      report();
    },
    unwatch(watchId) {
      const w = watches.get(watchId);
      if (!w) return false;
      w.observer.disconnect();
      watches.delete(watchId);
      return true;
    },
    apply(elementId, treatment, label) {
      const el = styled(elementOf(elementId));
      restore(el);
      if (treatment === "restore") return;
      const prev = { display: el.style.display, filter: el.style.filter, outline: el.style.outline, label: null };
      if (treatment === "hide") el.style.display = "none";
      else if (treatment === "blur") el.style.filter = "blur(8px)";
      else if (treatment === "highlight") el.style.outline = "3px solid currentColor";
      if (label !== void 0 || treatment === "label") {
        const tag = doc.createElement("div");
        tag.setAttribute(LABEL_ATTR, "");
        tag.textContent = label ?? "";
        el.prepend(tag);
        prev.label = tag;
      }
      applied.set(el, prev);
    },
    applyMany(items) {
      const done = [];
      const missing = [];
      for (const item of items) {
        try {
          this.apply(item.elementId, item.treatment, item.label);
          done.push(item.elementId);
        } catch (err) {
          if (!(err instanceof IntegrationError) || err.code !== "ELEMENT_NOT_FOUND") throw err;
          missing.push(item.elementId);
        }
      }
      return { applied: done, missing };
    },
    act(elementId, action, text) {
      const el = elementOf(elementId);
      if (action === "click") {
        if (el instanceof view.HTMLElement) el.click();
        else el.dispatchEvent(new view.MouseEvent("click", { bubbles: true }));
        return;
      }
      if (action === "focus") {
        styled(el).focus();
        return;
      }
      if (text === void 0) throw new IntegrationError('act "type" needs text', "VALIDATION_ERROR");
      if (!(el instanceof view.HTMLInputElement || el instanceof view.HTMLTextAreaElement)) {
        throw new IntegrationError(`Page element ${elementId} does not accept text`, "VALIDATION_ERROR");
      }
      const proto = el instanceof view.HTMLInputElement ? view.HTMLInputElement.prototype : view.HTMLTextAreaElement.prototype;
      Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, text);
      el.dispatchEvent(new view.Event("input", { bubbles: true }));
      el.dispatchEvent(new view.Event("change", { bubbles: true }));
    }
  };
}
var TREATMENTS = ["hide", "blur", "highlight", "label", "restore"];
function applyItems(params, name) {
  const v = params[name];
  if (!Array.isArray(v)) throw new IntegrationError(`Param '${name}' must be a list of { tab, elementId, treatment, label? }`, "VALIDATION_ERROR");
  return v.map((item, i) => {
    if (typeof item !== "object" || item === null || Array.isArray(item) || item instanceof Date) {
      throw new IntegrationError(`Param '${name}[${i}]' must be an object { tab, elementId, treatment, label? }`, "VALIDATION_ERROR");
    }
    const label = optStr(item, "label");
    return { tab: num(item, "tab"), elementId: str(item, "elementId"), treatment: oneOf(item, "treatment", TREATMENTS), ...label !== void 0 ? { label } : {} };
  });
}
var INTERACTIONS = ["click", "focus", "type"];
function matchesPattern(pattern, url) {
  if (pattern === "<all_urls>") return /^(https?|file|ftp):/.test(url);
  const m = /^(\*|https?|file|ftp):\/\/(\*|\*\.[^/*]+|[^/*]+)(\/.*)$/.exec(pattern);
  if (!m) return false;
  let u;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  const [, scheme, host, path2] = m;
  const proto = u.protocol.slice(0, -1);
  if (scheme === "*" ? proto !== "http" && proto !== "https" : proto !== scheme) return false;
  if (host !== "*") {
    if (host.startsWith("*.")) {
      const base = host.slice(2);
      if (u.hostname !== base && !u.hostname.endsWith(`.${base}`)) return false;
    } else if (u.hostname !== host) return false;
  }
  const escaped = path2.split("*").map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*");
  return new RegExp(`^${escaped}$`).test(u.pathname + u.search);
}
function createPageService(host) {
  const watches = /* @__PURE__ */ new Map();
  const byIdentity = /* @__PURE__ */ new Map();
  const attach = (tab, watchId, w) => host.send(tab, { kind: "watch", watchId, selector: w.selector, fields: w.fields });
  host.onTabReady((tab, url) => {
    for (const [watchId, w] of watches) if (matchesPattern(w.match, url)) void attach(tab, watchId, w);
  });
  host.onElement((tab, watchId, element, url) => {
    const w = watches.get(watchId);
    if (!w) return;
    w.deliver({ tab, elementId: element.elementId, url, fields: element.fields });
  });
  async function elements(tab, command) {
    const res = await host.send(tab, command);
    return "elements" in res ? res.elements : [];
  }
  const readyWaiters = /* @__PURE__ */ new Map();
  host.onTabReady((tab) => {
    const waiting = readyWaiters.get(tab) ?? [];
    readyWaiters.delete(tab);
    for (const wake of waiting) wake();
  });
  async function sendWhenReady(tab, command, waitMs) {
    const started = Date.now();
    try {
      return await elements(tab, command);
    } catch (err) {
      if (waitMs === void 0 || !(err instanceof IntegrationError) || err.code !== "PAGE_NOT_READY") throw err;
    }
    const left = waitMs - (Date.now() - started);
    const loaded = await new Promise((resolve) => {
      const timer = setTimeout(() => resolve(false), Math.max(0, left));
      readyWaiters.set(tab, [...readyWaiters.get(tab) ?? [], () => {
        clearTimeout(timer);
        resolve(true);
      }]);
    });
    if (!loaded) throw new IntegrationError(`Tab ${tab} did not load its page within ${waitMs}ms`, "PAGE_NOT_READY");
    const remaining = Math.max(0, waitMs - (Date.now() - started));
    return elements(tab, command.kind === "query" ? { ...command, waitMs: remaining } : command);
  }
  async function ok(tab, command) {
    const res = await host.send(tab, command);
    return "ok" in res ? res.ok : false;
  }
  function call(action, params, context) {
    return abortable(context?.signal, () => run(action, params, context));
  }
  async function run(action, params, context) {
    switch (action) {
      case "listTabs": {
        const match = optStr(params, "match");
        const tabs = await host.listTabs();
        return { tabs: match === void 0 ? tabs : tabs.filter((t) => matchesPattern(match, t.url)) };
      }
      case "openTab": {
        const url = new URL(str(params, "url"));
        for (const [name, value] of Object.entries(optStrMap(params, "params") ?? {})) url.searchParams.append(name, value);
        const match = optStr(params, "match");
        if (match !== void 0 && !matchesPattern(match, url.toString())) {
          throw new IntegrationError(`${url.toString()} is outside the declared pages ${match}`, "VALIDATION_ERROR");
        }
        return { tab: await host.openTab(url.toString(), bool(params, "active")) };
      }
      case "closeTab":
        await host.closeTab(num(params, "tab"));
        return { closed: true };
      case "navigate": {
        const url = str(params, "url");
        await host.navigate(num(params, "tab"), url);
        return { url };
      }
      case "query": {
        const tab = num(params, "tab");
        const waitMs = optNum(params, "waitMs");
        const command = { kind: "query", selector: str(params, "selector"), fields: fieldSpecs(params, "fields"), limit: optNum(params, "limit"), waitMs };
        return { tab, elements: await sendWhenReady(tab, command, waitMs) };
      }
      case "watch": {
        const app = context?.host;
        if (!app) throw new IntegrationError("page.watch needs a host that lends the running app (its declared inputs)", "NOT_CONFIGURED");
        const address = str(params, "event");
        const input = findInputByAddress(app.inputs(), address, app.caller);
        if (!input) {
          throw new IntegrationError(`'${address}' names no declared external input from ${app.caller.orbital}.${app.caller.trait}`, "VALIDATION_ERROR");
        }
        const match = str(params, "match");
        const selector = str(params, "selector");
        const fields = fieldSpecs(params, "fields");
        const identity = JSON.stringify([app.caller.orbital, app.caller.trait, match, selector, fields, address]);
        const existing = byIdentity.get(identity);
        if (existing !== void 0 && watches.has(existing)) return { watchId: existing };
        const w = {
          match,
          selector,
          fields,
          deliver: (payload) => {
            void app.dispatchInput(input.orbital, { targetTrait: input.trait, event: input.event, payload });
          }
        };
        const watchId = watchIdOf(identity);
        watches.set(watchId, w);
        byIdentity.set(identity, watchId);
        for (const t of await host.listTabs()) if (matchesPattern(w.match, t.url)) await attach(t.tab, watchId, w);
        return { watchId };
      }
      case "unwatch": {
        const watchId = str(params, "watchId");
        const stopped = watches.delete(watchId);
        if (stopped) for (const t of await host.listTabs()) await host.send(t.tab, { kind: "unwatch", watchId });
        return { stopped };
      }
      case "apply": {
        const tab = num(params, "tab");
        const elementId = str(params, "elementId");
        const treatment = oneOf(params, "treatment", TREATMENTS);
        const applied = await ok(tab, { kind: "apply", elementId, treatment, label: optStr(params, "label") });
        return { applied, tab, elementId, treatment };
      }
      case "applyMany": {
        const items = applyItems(params, "items");
        const tabs = Array.from(new Set(items.map((i) => i.tab)));
        const applied = [];
        const missing = [];
        for (const tab of tabs) {
          const mine = items.filter((i) => i.tab === tab);
          const res = await host.send(tab, { kind: "applyMany", items: mine.map(({ elementId, treatment, label }) => ({ elementId, treatment, ...label !== void 0 ? { label } : {} })) }).catch(() => void 0);
          if (res === void 0 || !("applied" in res)) {
            missing.push(...mine.map(({ elementId }) => ({ tab, elementId })));
            continue;
          }
          applied.push(...res.applied.map((elementId) => ({ tab, elementId })));
          missing.push(...res.missing.map((elementId) => ({ tab, elementId })));
        }
        return { applied, missing };
      }
      case "act": {
        const tab = num(params, "tab");
        const elementId = str(params, "elementId");
        const done = await ok(tab, { kind: "act", elementId, action: oneOf(params, "action", INTERACTIONS), text: optStr(params, "text") });
        return { done, tab, elementId };
      }
      default:
        throw new IntegrationError(`page has no action '${action}'`, "VALIDATION_ERROR");
    }
  }
  return { call };
}
function watchIdOf(identity) {
  let hash = 2166136261;
  for (let i = 0; i < identity.length; i++) {
    hash ^= identity.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return `w-${hash.toString(16).padStart(8, "0")}`;
}

// src/integrations/page/chrome.ts
function chromePageHost(tabs, runtime) {
  const ready = [];
  const elements = [];
  runtime.onMessage.addListener((message, sender) => {
    const tab = sender.tab?.id;
    if (tab === void 0) return;
    if (message.almadarPage === "ready") ready.forEach((l) => l(tab, message.url));
    else if (message.almadarPage === "element") elements.forEach((l) => l(tab, message.watchId, message.element, message.url));
  });
  const toTab = (t) => t.id === void 0 ? [] : [{ tab: t.id, url: t.url ?? "", title: t.title ?? "", active: t.active === true }];
  return {
    listTabs: async () => (await tabs.query({})).flatMap(toTab),
    openTab: async (url, active) => {
      const t = await tabs.create({ url, active });
      if (t.id === void 0) throw new IntegrationError(`Chrome did not open ${url}`, "SERVICE_ERROR");
      return t.id;
    },
    closeTab: (tab) => tabs.remove(tab),
    navigate: async (tab, url) => {
      await tabs.update(tab, { url });
    },
    send: async (tab, command) => {
      const reply = await tabs.sendMessage(tab, { almadarPage: "command", command }).catch(() => void 0);
      if (reply === void 0) throw new IntegrationError(`Tab ${tab} has no page script`, "PAGE_NOT_READY");
      if ("error" in reply) throw new IntegrationError(reply.error.message, reply.error.code === "ELEMENT_NOT_FOUND" ? "ELEMENT_NOT_FOUND" : "SERVICE_ERROR");
      return reply;
    },
    onTabReady: (l) => ready.push(l),
    onElement: (l) => elements.push(l)
  };
}
function runPageContentScript(runtime, doc, url) {
  const executor = createPageExecutor(doc);
  const run = async (command) => {
    switch (command.kind) {
      case "query":
        return {
          elements: command.waitMs !== void 0 ? await executor.queryWhenPresent(command.selector, command.fields, command.limit, command.waitMs) : executor.query(command.selector, command.fields, command.limit)
        };
      case "watch":
        executor.watch(command.watchId, command.selector, command.fields, (element) => {
          void runtime.sendMessage({ almadarPage: "element", watchId: command.watchId, element, url });
        });
        return { ok: true };
      case "unwatch":
        return { ok: executor.unwatch(command.watchId) };
      case "apply":
        executor.apply(command.elementId, command.treatment, command.label);
        return { ok: true };
      case "applyMany":
        return executor.applyMany(command.items);
      case "act":
        executor.act(command.elementId, command.action, command.text);
        return { ok: true };
    }
  };
  runtime.onMessage.addListener((message, _sender, respond) => {
    if (message.almadarPage !== "command") return;
    run(message.command).then(respond, (err) => {
      const code = err instanceof IntegrationError ? err.code : "SERVICE_ERROR";
      respond({ error: { code, message: err.message } });
    });
    return true;
  });
  void runtime.sendMessage({ almadarPage: "ready", url });
}
function classifyManyInstructions(categories) {
  return [
    "Classify each item below into exactly one of these categories:",
    categories.map((c) => `- ${c}`).join("\n"),
    "Answer with one verdict per item: its id, the category, and your confidence from 0 to 1."
  ].join("\n");
}
var labelOf = (index) => String(index + 1);
function classifyManyItems(items) {
  return items.map((item, i) => `[${labelOf(i)}] ${item.text}`).join("\n");
}
function classifyManyJsonSchema(items, categories) {
  return {
    type: "object",
    properties: {
      verdicts: {
        type: "array",
        minItems: items.length,
        maxItems: items.length,
        items: {
          type: "object",
          properties: {
            id: { type: "string", enum: items.map((_item, i) => labelOf(i)) },
            category: { type: "string", enum: [...categories] },
            confidence: { type: "number", minimum: 0, maximum: 1 }
          },
          required: ["id", "category", "confidence"]
        }
      }
    },
    required: ["verdicts"]
  };
}
function classifyManyZodSchema(categories) {
  return z.object({
    verdicts: z.array(z.object({ id: z.string(), category: z.string(), confidence: z.number().min(0).max(1) }))
  }).refine((r) => r.verdicts.every((v) => categories.includes(v.category)), "every category must be a declared one");
}
function checkVerdicts(items, categories, verdicts, reply) {
  const byLabel = /* @__PURE__ */ new Map();
  for (const v of verdicts) {
    const index = items.findIndex((_item, i) => labelOf(i) === v.id);
    if (index < 0) throw new IntegrationError(`The model gave a verdict for item '${v.id}', which was not sent`, "SERVICE_ERROR", { reply });
    if (byLabel.has(v.id)) throw new IntegrationError(`The model gave item '${v.id}' two verdicts`, "SERVICE_ERROR", { reply });
    if (!categories.includes(v.category)) throw new IntegrationError(`The model chose '${v.category}' for item '${v.id}', not a declared category`, "SERVICE_ERROR", { reply });
    byLabel.set(v.id, { id: items[index].id, category: v.category, confidence: v.confidence });
  }
  const ordered = [];
  const missing = [];
  items.forEach((item, i) => {
    const verdict = byLabel.get(labelOf(i));
    if (verdict === void 0) missing.push(item.id);
    else ordered.push(verdict);
  });
  if (missing.length > 0) throw new IntegrationError(`The model gave no verdict for ${missing.join(", ")}`, "SERVICE_ERROR", { reply });
  return { verdicts: ordered };
}

// src/integrations/llm/browser.ts
var SERVER_ONLY_ACTIONS = /* @__PURE__ */ new Set(["generate", "extract", "summarize", "call-tools"]);
function createBrowserLlm(model, embedder) {
  async function classify(params) {
    const text = str(params, "text");
    const categories = strList(params, "categories");
    if (categories.length === 0) throw new IntegrationError("Param 'categories' must name at least one category", "VALIDATION_ERROR");
    const state = await model.availability();
    if (state !== "available") throw new IntegrationError(`The on-device model is ${state}`, "MODEL_UNAVAILABLE", { state });
    const schema = {
      type: "object",
      properties: {
        category: { type: "string", enum: categories },
        confidence: { type: "number", minimum: 0, maximum: 1 },
        reasoning: { type: "string" }
      },
      required: ["category", "confidence", "reasoning"]
    };
    const prompt = [
      "Classify the text into exactly one of these categories:",
      categories.map((c) => `- ${c}`).join("\n"),
      "Answer with the category, your confidence from 0 to 1, and one short sentence of reasoning.",
      "",
      `Text: ${text}`
    ].join("\n");
    const reply = await model.prompt(prompt, schema);
    const verdict = z.object({ category: z.string(), confidence: z.number().min(0).max(1), reasoning: z.string() }).safeParse(parseJson(reply));
    if (!verdict.success) throw new IntegrationError("The on-device model replied outside the declared shape", "SERVICE_ERROR", { reply });
    if (!categories.includes(verdict.data.category)) {
      throw new IntegrationError(`The on-device model chose '${verdict.data.category}', not a declared category`, "SERVICE_ERROR", { reply });
    }
    return verdict.data;
  }
  async function classifyMany(params, signal) {
    const items = idTextItems(params, "items");
    const categories = strList(params, "categories");
    if (categories.length === 0) throw new IntegrationError("Param 'categories' must name at least one category", "VALIDATION_ERROR");
    if (items.length === 0) return { verdicts: [] };
    if (isAborted(signal)) throw cancelled();
    const state = await model.availability();
    if (state !== "available") throw new IntegrationError(`The on-device model is ${state}`, "MODEL_UNAVAILABLE", { state });
    let reply;
    try {
      reply = await model.prompt(`${classifyManyInstructions(categories)}

${classifyManyItems(items)}`, classifyManyJsonSchema(items, categories), signal);
    } catch (err) {
      if (isAborted(signal)) throw cancelled();
      throw err;
    }
    const parsed = classifyManyZodSchema(categories).safeParse(parseJson(reply));
    if (!parsed.success) {
      const shape = z.object({ verdicts: z.array(z.object({ id: z.string(), category: z.string(), confidence: z.number() })) }).safeParse(parseJson(reply));
      if (!shape.success) throw new IntegrationError("The on-device model replied outside the declared shape", "SERVICE_ERROR", { reply });
      return checkVerdicts(items, categories, shape.data.verdicts, reply);
    }
    return checkVerdicts(items, categories, parsed.data.verdicts, reply);
  }
  async function embed(params, signal) {
    const texts = strList(params, "texts");
    if (embedder === void 0) throw new IntegrationError("No on-device embedder is wired into this host", "NOT_CONFIGURED");
    if (texts.length === 0) return { embeddings: [] };
    if (isAborted(signal)) throw cancelled();
    let embeddings;
    try {
      embeddings = await abortable(signal, () => embedder.embed(texts, signal));
    } catch (err) {
      if (isAborted(signal)) throw cancelled();
      throw err;
    }
    if (embeddings.length !== texts.length) {
      throw new IntegrationError(`The on-device embedder returned ${embeddings.length} vectors for ${texts.length} texts`, "SERVICE_ERROR");
    }
    return { embeddings };
  }
  async function call(action, params, context) {
    if (action === "classify") return classify(params);
    if (action === "embed") return embed(params, context?.signal);
    if (action === "classifyMany") return classifyMany(params, context?.signal);
    if (SERVER_ONLY_ACTIONS.has(action)) {
      throw new IntegrationError(`llm '${action}' has no on-device implementation`, "ACTION_UNAVAILABLE_IN_RUNTIME");
    }
    throw new IntegrationError(`llm has no action '${action}'`, "VALIDATION_ERROR");
  }
  return { call };
}
function parseJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// src/integrations/llm/embedder.ts
function transformersEmbedder(load, options = {}) {
  const prefix = options.prefix ?? "";
  let loading;
  return {
    async embed(texts, signal) {
      if (isAborted(signal)) throw cancelled();
      loading ?? (loading = load().catch((err) => {
        loading = void 0;
        throw err;
      }));
      const extract = await loading;
      if (isAborted(signal)) throw cancelled();
      const output = await extract(texts.map((t) => prefix + t), { pooling: "mean", normalize: true });
      return output.tolist();
    }
  };
}

// src/integrations/llm/chrome.ts
var PROMPT_TIMEOUT_MS = 3e4;
function chromePromptModel(lookup, options = {}) {
  const timeoutMs = options.timeoutMs ?? PROMPT_TIMEOUT_MS;
  let base;
  return {
    availability: async () => {
      const lm = lookup();
      return lm ? lm.availability() : "unavailable";
    },
    prompt: async (text, schema, signal) => {
      const lm = lookup();
      if (!lm) throw new IntegrationError("This browser has no Prompt API", "MODEL_UNAVAILABLE");
      if (isAborted(signal)) throw cancelled();
      base ?? (base = lm.create());
      let session;
      try {
        session = await (await base).clone();
      } catch (err) {
        base = void 0;
        throw err;
      }
      if (isAborted(signal)) {
        session.destroy();
        throw cancelled();
      }
      const abort = new AbortController();
      let timer;
      let onCallerAbort;
      const stopped = new Promise((_resolve, reject) => {
        timer = setTimeout(() => {
          reject(new IntegrationError(`The on-device model did not answer within ${timeoutMs}ms`, "TIMEOUT_ERROR", { timeoutMs }));
          abort.abort();
        }, timeoutMs);
        onCallerAbort = () => {
          reject(cancelled());
          abort.abort();
        };
        signal?.addEventListener("abort", onCallerAbort, { once: true });
      });
      try {
        return await Promise.race([session.prompt(text, { responseConstraint: schema, signal: abort.signal }), stopped]);
      } finally {
        clearTimeout(timer);
        if (onCallerAbort !== void 0) signal?.removeEventListener("abort", onCallerAbort);
        session.destroy();
      }
    }
  };
}
var DAY_MS = 864e5;
var DEFAULT_LIMIT = 5;
var DEFAULT_SINCE_DAYS = 30;
var JumpBackIn = z.object({
  items: z.array(
    z.object({
      title: z.string(),
      lastAccessedAt: z.number(),
      progress: z.object({ progressPercentage: z.number() }).optional()
    })
  )
});
var Goals = z.object({ goals: z.array(z.object({ title: z.string(), target: z.string(), updatedAt: z.number() })) });
var Activity = z.object({ activity: z.array(z.object({ type: z.string(), resourceName: z.string(), timestamp: z.number() })) });
function createKnowledgeService(options) {
  const now = options.now ?? Date.now;
  async function get(path2, schema, token) {
    let res;
    try {
      res = await options.fetch(new URL(path2, options.baseUrl), { headers: { authorization: `Bearer ${token}` } });
    } catch (err) {
      throw new IntegrationError(`KFlow ${path2} is unreachable`, "NETWORK_ERROR", { error: String(err) });
    }
    if (res.status === 401 || res.status === 403) throw new IntegrationError("KFlow rejected the sign-in", "AUTH_ERROR");
    if (!res.ok) throw new IntegrationError(`KFlow ${path2} answered ${res.status}`, "SERVICE_ERROR");
    const parsed = schema.safeParse(await res.json());
    if (!parsed.success) throw new IntegrationError(`KFlow ${path2} answered an unexpected shape`, "SERVICE_ERROR", { issues: parsed.error.issues });
    return parsed.data;
  }
  async function currentTopics(params) {
    const limit = optNum(params, "limit") ?? DEFAULT_LIMIT;
    const sinceDays = optNum(params, "sinceDays") ?? DEFAULT_SINCE_DAYS;
    const token = await options.auth.idToken();
    const [paths, goals, activity] = await Promise.all([
      get("/api/user/jump-back-in", JumpBackIn, token),
      get("/api/learning/goals", Goals, token),
      get("/api/user/statistics/recent-activity?limit=50", Activity, token)
    ]);
    const t = now();
    const recency = (at) => Math.max(0, 1 - (t - at) / DAY_MS / sinceDays);
    const byLabel = /* @__PURE__ */ new Map();
    const add = (label, keywords, weight) => {
      if (weight <= 0) return;
      const prev = byLabel.get(label);
      if (!prev) byLabel.set(label, { label, keywords, weight });
      else byLabel.set(label, { label, keywords: [.../* @__PURE__ */ new Set([...prev.keywords, ...keywords])], weight: Math.max(prev.weight, weight) });
    };
    for (const p of paths.items) {
      const done = p.progress?.progressPercentage ?? 0;
      if (done >= 100) continue;
      add(p.title, [], recency(p.lastAccessedAt) * (1 - done / 100));
    }
    for (const g of goals.goals) add(g.title, g.target === "" ? [] : [g.target], recency(g.updatedAt));
    for (const a of activity.activity) if (a.type === "concept_studied") add(a.resourceName, [], recency(a.timestamp));
    const topics = [...byLabel.values()].sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label));
    return { topics: topics.slice(0, limit) };
  }
  async function call(action, params) {
    switch (action) {
      case "status":
        return options.auth.status();
      case "connect":
        return options.auth.connect();
      case "currentTopics":
        return currentTopics(params);
      default:
        throw new IntegrationError(`knowledge has no action '${action}'`, "VALIDATION_ERROR");
    }
  }
  return { call };
}

// src/integrations/knowledge/chrome.ts
var SESSION_KEY = "almadar.knowledge.session";
function firebaseKnowledgeAuth(options) {
  const now = options.now ?? Date.now;
  async function load() {
    const raw = (await options.storage.get(SESSION_KEY))[SESSION_KEY];
    if (!raw) return null;
    const s = { idToken: "", refreshToken: "", expiresAt: 0, account: "", ...raw };
    return s.idToken !== "" && s.refreshToken !== "" ? s : null;
  }
  async function post(url, body, form) {
    const res = await options.fetch(url, {
      method: "POST",
      headers: { "content-type": form ? "application/x-www-form-urlencoded" : "application/json" },
      body: form ? new URLSearchParams(Object.entries(body).map(([k, v]) => [k, String(v)])).toString() : JSON.stringify(body)
    });
    if (!res.ok) throw new IntegrationError(`Sign-in failed (${res.status})`, "AUTH_ERROR");
    const json = await res.json();
    return json;
  }
  return {
    async status() {
      const s = await load();
      return { connected: s !== null, account: s?.account ?? "" };
    },
    async connect() {
      const redirect = options.identity.getRedirectURL();
      const auth = new URL("https://accounts.google.com/o/oauth2/v2/auth");
      auth.search = new URLSearchParams({
        client_id: options.googleClientId,
        response_type: "id_token",
        redirect_uri: redirect,
        scope: "openid email",
        nonce: String(now())
      }).toString();
      const returned = await options.identity.launchWebAuthFlow({ url: auth.toString(), interactive: true });
      const googleIdToken = returned ? new URLSearchParams(new URL(returned).hash.slice(1)).get("id_token") : null;
      if (!googleIdToken) throw new IntegrationError("Sign-in was cancelled", "AUTH_ERROR");
      const signed = await post(
        `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${encodeURIComponent(options.apiKey)}`,
        { postBody: `id_token=${googleIdToken}&providerId=google.com`, requestUri: redirect, returnSecureToken: true },
        false
      );
      const session = {
        idToken: signed.idToken,
        refreshToken: signed.refreshToken,
        expiresAt: now() + Number(signed.expiresIn) * 1e3,
        account: signed.email ?? ""
      };
      await options.storage.set({ [SESSION_KEY]: session });
      return { connected: true, account: session.account };
    },
    async idToken() {
      const s = await load();
      if (!s) throw new IntegrationError("No learner is connected", "AUTH_ERROR");
      if (now() < s.expiresAt - 6e4) return s.idToken;
      const refreshed = await post(
        `https://securetoken.googleapis.com/v1/token?key=${encodeURIComponent(options.apiKey)}`,
        { grant_type: "refresh_token", refresh_token: s.refreshToken },
        true
      );
      const next = { ...s, idToken: refreshed.id_token, refreshToken: refreshed.refresh_token, expiresAt: now() + Number(refreshed.expires_in) * 1e3 };
      await options.storage.set({ [SESSION_KEY]: next });
      return next.idToken;
    }
  };
}

// src/browser/index.ts
function isIntegrationName(name) {
  return Object.prototype.hasOwnProperty.call(serviceRuntimes, name);
}
var BROWSER_SERVICES = Object.keys(serviceRuntimes).filter(isIntegrationName).filter((name) => serviceRuntimes[name].includes("browser"));
function createBrowserCallService(services) {
  return async (service, action, params, context) => {
    const name = BROWSER_SERVICES.find((s) => s === service);
    if (name === void 0) {
      throw new IntegrationError(`Service '${service}' declares no browser implementation`, "RUNTIME_UNSUPPORTED");
    }
    const impl = services[name];
    if (!impl) throw new IntegrationError(`Service '${service}' is not wired in this browser host`, "NOT_CONFIGURED");
    return impl.call(action, params ?? {}, context);
  };
}
var WEB_HOSTED = {
  analytics: () => createAnalyticsService()
};
function webBrowserServices(invoked) {
  const services = {};
  for (const name of BROWSER_SERVICES) {
    const make = WEB_HOSTED[name];
    if (make !== void 0 && invoked.includes(name)) services[name] = make();
  }
  return services;
}

export { BROWSER_SERVICES, PROMPT_TIMEOUT_MS, browserPage, chromePageHost, chromePromptModel, createAnalyticsService, createBrowserCallService, createBrowserLlm, createKnowledgeService, createPageExecutor, createPageService, firebaseKnowledgeAuth, matchesPattern, runPageContentScript, transformersEmbedder, webBrowserServices };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map